import { Trash2, Undo2 } from 'lucide-react';
import { useId, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo, CampoTexto } from '@/componentes/Campo';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { Janela } from '@/componentes/JanelaDeAcao';
import { formatarAteDuasCasas, formatarDataHora, interpretarDataHora, interpretarNumero, paraCampoDataHora } from '@/lib/formato';
import type { ContextoDoRetorno } from '@/lib/retorno';

import { useCorrigirAbastecimento, useEstornarAbastecimento, useExcluirAbastecimento } from './consultas';
import {
  PISO_DO_MOTIVO_DO_ESTORNO,
  correcaoMudaAlgo,
  envioDaCorrecao,
  originalDoDominio,
  type CamposDaCorrecao,
} from './estorno';
import type { Abastecimento } from './tipos';

/**
 * ESTORNAR E CORRIGIR O ABASTECIMENTO na mesa (ADR-008, migração 0087).
 *
 * As duas ações moram nos três pontos da linha dos lançamentos, como o estorno
 * do livro do tanque: são exceção, não o caminho do dia, e nenhuma é laranja —
 * o primário da tela é "Lançar abastecimento". A confirmação nomeia o que muda.
 *
 * A CORREÇÃO muda o que costuma estar errado — litros, data, medidor e, no
 * externo, o preço e o cupom — e mantém a máquina, o tanque ou posto, a obra e
 * as pessoas. Quem lançou na máquina errada estorna e lança de novo na certa:
 * trocar a máquina numa "correção" esconderia que o consumo mudou de dono.
 *
 * O desenho é o da R06 (p.69): a confirmação pergunta nomeando o alvo
 * ("Estornar o abastecimento de 320 L?"), diz a consequência e pede o motivo
 * com o contador; Estornar, em vermelho sólido, só liga com o piso. Na janela
 * de correção, Estornar fica à esquerda em tinta vermelha e troca a janela
 * pela confirmação (nunca dois modais empilhados); Salvar correção é o
 * primário. O piso é o de `org.estorno` (15, ADR-008), e não os 10 da p.69.
 */
export type AcaoNoAbastecimento = 'estorno' | 'correcao' | 'exclusao';

function contextoDo(a: Abastecimento): ContextoDoRetorno {
  return {
    alvo: { codigo: a.equipamentoCodigo },
    fatos: [`${formatarAteDuasCasas(a.litros)} L`, formatarDataHora(a.quando)],
  };
}

function origemDe(a: Abastecimento): string {
  return a.tipo === 'externo' ? (a.posto ?? 'o posto') : (a.tanque ?? 'o tanque');
}

export function JanelasDoAbastecimento({
  acao,
  alvo,
  aoFechar,
  aoTrocarParaEstorno,
}: {
  readonly acao: AcaoNoAbastecimento | null;
  readonly alvo: Abastecimento | null;
  readonly aoFechar: () => void;
  /** Estornar, de dentro da correção: a janela vira a confirmação (p.69). */
  readonly aoTrocarParaEstorno?: () => void;
}) {
  const base = useId();
  const estornar = useEstornarAbastecimento();
  const corrigir = useCorrigirAbastecimento();
  const excluir = useExcluirAbastecimento();
  const [motivo, setMotivo] = useState('');
  const [litros, setLitros] = useState('');
  const [quando, setQuando] = useState('');
  const [medidor, setMedidor] = useState('');
  const [preco, setPreco] = useState('');
  const [comprovante, setComprovante] = useState('');
  const [abertoPara, setAbertoPara] = useState<string | null>(null);

  /* Os campos da correção abrem com o lançamento errado, uma vez por abertura. */
  const chave = alvo === null || acao === null ? null : `${acao}:${alvo.id}`;
  if (chave !== abertoPara) {
    setAbertoPara(chave);
    setMotivo('');
    if (alvo !== null) {
      setLitros(formatarAteDuasCasas(alvo.litros));
      setQuando(paraCampoDataHora(alvo.quando));
      setMedidor(alvo.horimetro === null ? '' : formatarAteDuasCasas(alvo.horimetro));
      setPreco(alvo.precoLitro === null ? '' : formatarAteDuasCasas(alvo.precoLitro));
      setComprovante(alvo.comprovante ?? '');
    }
  }

  function fechar() {
    estornar.reset();
    corrigir.reset();
    excluir.reset();
    aoFechar();
  }

  if (alvo === null) return null;
  const original = originalDoDominio(alvo);
  const externo = alvo.tipo === 'externo';
  const curto = motivo.trim().length < PISO_DO_MOTIVO_DO_ESTORNO;
  const rotuloDoMedidor = alvo.tipoMedidor === 'odometro' ? 'Odômetro' : 'Horímetro';

  const campos: CamposDaCorrecao | null = (() => {
    const l = interpretarNumero(litros);
    const q = interpretarDataHora(quando);
    const m = medidor.trim() === '' ? null : interpretarNumero(medidor);
    const p = preco.trim() === '' ? null : interpretarNumero(preco);
    if (l === null || l <= 0 || q === null) return null;
    if (medidor.trim() !== '' && (m === null || m < 0)) return null;
    if (externo && (p === null || p < 0 || comprovante.trim() === '')) return null;
    return { litros: l, quando: q, medidor: m, precoLitro: externo ? p : null, comprovante: externo ? comprovante.trim() : null };
  })();
  const mudou = campos !== null && original !== null && correcaoMudaAlgo(original, campos);

  const textoDoMotivo = (
    <CampoDoMotivo minimo={PISO_DO_MOTIVO_DO_ESTORNO} valor={motivo} aoMudar={setMotivo} exemplo="Lançado na máquina errada, era a ESC-009" />
  );

  /* APAGAR (0126) — a janela é a mesma confirmação do estorno, e a diferença
     está no que ela promete. O estorno diz "o lançamento fica visível como
     estornado"; esta diz que a linha sai e onde a cópia dela fica. Prometer
     menos do que isso seria esconder que a lista vai mudar de soma. */
  const janelaDeExclusao = (
    <Janela
      titulo={`Apagar o abastecimento de ${formatarAteDuasCasas(alvo.litros)} L na ${alvo.equipamentoCodigo}?`}
      largura="p"
      aberta={acao === 'exclusao'}
      aoFechar={fechar}
      acoes={
        <>
          <Botao nivel="secundario" onClick={fechar}>
            Manter abastecimento
          </Botao>
          <button
            type="button"
            className="botao botao-destrutivo-solido"
            disabled={curto || excluir.isPending}
            onClick={() => {
              excluir.mutate({ id: alvo.id, motivo: motivo.trim(), contexto: contextoDo(alvo) }, { onSuccess: fechar });
            }}
          >
            {excluir.isPending ? 'Apagando…' : 'Apagar'}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <span aria-hidden="true" className="flex size-12 items-center justify-center rounded-controle bg-erro-fundo">
          <Trash2 className="size-[var(--icone-destaque)] text-erro" />
        </span>
        {excluir.isError ? (
          <p role="alert" className="txt-apoio text-parada">{mensagemDoErro(excluir.error, 'A exclusão não foi aceita')}</p>
        ) : null}
        <p className="txt-corpo text-pretty text-tinta-secundaria">
          <span className="codigo text-tinta">{alvo.equipamentoCodigo}</span>, {formatarDataHora(alvo.quando)}.{' '}
          {externo
            ? `Os ${formatarAteDuasCasas(alvo.litros)} L saem da lista e da cobrança do posto.`
            : `Os ${formatarAteDuasCasas(alvo.litros)} L voltam ao saldo de ${origemDe(alvo)} e a linha sai da lista.`}{' '}
          Isto não é o estorno: a linha deixa de existir. A cópia dela fica no registro de exclusões, com este motivo e com
          o seu nome.
        </p>
        {textoDoMotivo}
      </div>
    </Janela>
  );

  return (
    <>
      {janelaDeExclusao}
      <Janela
        titulo={`Estornar o abastecimento de ${formatarAteDuasCasas(alvo.litros)} L na ${alvo.equipamentoCodigo}?`}
        largura="p"
        aberta={acao === 'estorno'}
        aoFechar={fechar}
        acoes={
          <>
            <Botao nivel="secundario" onClick={fechar}>
              Manter abastecimento
            </Botao>
            {/* Vermelho sólido só aqui, na decisão final (p.69). */}
            <button
              type="button"
              className="botao botao-destrutivo-solido"
              disabled={curto || estornar.isPending}
              onClick={() => {
                estornar.mutate({ id: alvo.id, motivo: motivo.trim(), contexto: contextoDo(alvo) }, { onSuccess: fechar });
              }}
            >
              {estornar.isPending ? 'Estornando…' : 'Estornar'}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <span aria-hidden="true" className="flex size-12 items-center justify-center rounded-controle bg-erro-fundo">
            <Undo2 className="size-[var(--icone-destaque)] text-erro" />
          </span>
          {/* A recusa no TOPO do formulário (p.22). */}
          {estornar.isError ? (
            <p role="alert" className="txt-apoio text-parada">{mensagemDoErro(estornar.error, 'O estorno não foi aceito')}</p>
          ) : null}
          <p className="txt-corpo text-pretty text-tinta-secundaria">
            <span className="codigo text-tinta">{alvo.equipamentoCodigo}</span>, {formatarDataHora(alvo.quando)}.{' '}
            {externo
              ? `Os ${formatarAteDuasCasas(alvo.litros)} L saem da cobrança do posto, se ela ainda não foi conciliada.`
              : `Os ${formatarAteDuasCasas(alvo.litros)} L voltam ao saldo de ${origemDe(alvo)}.`}{' '}
            O lançamento fica visível como estornado.
          </p>
          {textoDoMotivo}
        </div>
      </Janela>

      <Janela
        titulo="Corrigir abastecimento"
        descricao={`${alvo.equipamentoCodigo} · ${formatarDataHora(alvo.quando)}. O lançamento errado é estornado e o certo entra no lugar dele, numa operação só.`}
        aberta={acao === 'correcao'}
        aoFechar={fechar}
        acoes={
          <>
            {/* Estornar à esquerda, em tinta vermelha (p.69): troca a janela pela confirmação. */}
            {aoTrocarParaEstorno ? (
              <Botao
                nivel="destrutivo"
                className="mr-auto"
                onClick={() => {
                  corrigir.reset();
                  aoTrocarParaEstorno();
                }}
              >
                <Undo2 aria-hidden="true" className="size-[var(--icone-controle)]" />
                Estornar
              </Botao>
            ) : null}
            <Botao nivel="terciario" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao
              nivel="primario"
              disabled={curto || !mudou || corrigir.isPending || original === null}
              onClick={() => {
                if (campos === null || original === null) return;
                corrigir.mutate(
                  { id: alvo.id, motivo: motivo.trim(), envio: envioDaCorrecao(original, campos), antes: alvo.litros, contexto: contextoDo(alvo) },
                  { onSuccess: fechar },
                );
              }}
            >
              {corrigir.isPending ? 'Salvando…' : 'Salvar correção'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {corrigir.isError ? (
            <p role="alert" className="txt-apoio text-parada">{mensagemDoErro(corrigir.error, 'A correção não foi aceita')}</p>
          ) : null}
          <p className="text-tinta-secundaria">
            <span className="codigo text-tinta">{alvo.equipamentoCodigo}</span>, {alvo.produto ?? 'combustível'}, de {origemDe(alvo)}.
            Lançou na máquina errada? Estorne este e lance de novo na máquina certa.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <CampoTexto
              rotulo="Litros certos"
              valor={litros}
              aoMudar={setLitros}
              placeholder="38"
              validar={(v) => {
                const n = interpretarNumero(v);
                return n === null || n <= 0 ? 'Informe os litros que de fato entraram na máquina.' : null;
              }}
            />
            <Campo id={`${base}-quando`} rotulo="Data e hora">
              <input
                id={`${base}-quando`}
                type="datetime-local"
                value={quando}
                className="controle w-full"
                onChange={(e) => {
                  setQuando(e.target.value);
                }}
              />
            </Campo>
            <CampoTexto
              rotulo={rotuloDoMedidor}
              opcional
              valor={medidor}
              aoMudar={setMedidor}
              placeholder="4.832"
              ajuda="A leitura errada sai com o estorno; deixe em branco se não houve leitura."
              validar={(v) => {
                if (v.trim() === '') return null;
                const n = interpretarNumero(v);
                return n === null || n < 0 ? `Informe o ${rotuloDoMedidor.toLowerCase()} do momento do abastecimento.` : null;
              }}
            />
            {externo ? (
              <>
                <CampoTexto
                  rotulo="Preço do litro"
                  valor={preco}
                  aoMudar={setPreco}
                  placeholder="6,29"
                  validar={(v) => {
                    const n = interpretarNumero(v);
                    return n === null || n < 0 ? 'Informe o preço do litro do cupom, como 6,29.' : null;
                  }}
                />
                <CampoTexto
                  rotulo="Cupom ou nota"
                  valor={comprovante}
                  aoMudar={setComprovante}
                  placeholder="CF 001234"
                  validar={(v) => (v.trim() === '' ? 'Informe o número do cupom ou da nota do posto.' : null)}
                />
              </>
            ) : null}
          </div>
          {campos !== null && !mudou ? (
            <p className="txt-apoio text-tinta-secundaria">Mude o que estava errado: a correção igual ao lançamento não corrige nada.</p>
          ) : null}
          {textoDoMotivo}
        </div>
      </Janela>
    </>
  );
}

/**
 * A recusa do servidor já vem escrita para quem opera (`comoResolver`), e é
 * ela que a tela mostra — a cobrança conciliada, o tanque que não comporta.
 */
function mensagemDoErro(erro: unknown, padrao: string): string {
  const corpo = (erro as { corpo?: { erro?: string; comoResolver?: string } } | null)?.corpo;
  if (corpo?.comoResolver) return `${corpo.erro ?? padrao}. ${corpo.comoResolver}`;
  return `${padrao}: o pedido não chegou ao servidor. Tente outra vez; se continuar, avise o suporte com o horário.`;
}
